import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { getExhibitions } from '@/lib/data/exhibitions';

const DEFAULT_IMAGE = '/images/default-image.jpg';

export default function FooterContent() {
    const [exhibitions, setExhibitions] = useState([]);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            const all = await getExhibitions({ limit: 50 });
            if (cancelled) return;
            const now = Date.now();
            const upcoming = all
                .filter((e) => {
                    const end = e.end_date ? new Date(e.end_date).getTime() : null;
                    return end === null || end >= now;
                })
                .slice(0, 3);
            setExhibitions(upcoming);
        };
        load();
        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <div className='flex flex-col md:grid md:grid-cols-3 gap-8 p-6 md:p-6 lg:p-10 md:max-h-[80vh] md:overflow-y-auto'>
            <div className='md:col-span-1 md:row-span-1 mb-8 md:mb-0'>
                <MehrVonMir />
            </div>
            <div className='md:col-span-1 md:row-span-1 border-t md:border-t-0 md:border-l border-white/20 pt-8 md:pt-0 md:px-6'>
                <UpcomingExhibitions exhibitions={exhibitions} />
            </div>
            <div className='md:col-span-1 md:row-span-1 border-t md:border-t-0 md:border-l border-white/20 pt-8 md:pt-0 md:px-6'>
                <NavLegalSocial />
            </div>
            <div className='col-span-3 text-center mt-4 pt-2 border-t border-white/20'>
                <p className='text-white/60 text-xs md:text-sm'>© 2026 Oppermann Kunst. All rights reserved.</p>
            </div>
        </div>
    );
}

const NavLegalSocial = () => {
    return (
        <div className='text-white/80 flex flex-col h-full md:items-end'>
            <div className='mb-4 md:mb-6 md:text-right'>
                <h3 className='mb-2 md:mb-3 uppercase text-white/90 font-semibold text-sm md:text-base'>Navigation</h3>
                <ul className='space-y-1 md:space-y-2'>
                    <li>
                        <Link href="/" className='text-sm md:text-base hover:text-white transition-colors group relative inline-block overflow-hidden'>
                            <span className="absolute right-0 transition-all duration-500 ease-in-out translate-x-full group-hover:translate-x-0">Home</span>
                            <span className="inline-block transition-all duration-500 ease-in-out group-hover:-translate-x-full">Startseite</span>
                        </Link>
                    </li>
                    <li>
                        <Link href="/about" className='text-sm md:text-base hover:text-white transition-colors group relative inline-block overflow-hidden'>
                            <span className="absolute right-0 transition-all duration-500 ease-in-out translate-x-full group-hover:translate-x-0">About</span>
                            <span className="inline-block transition-all duration-500 ease-in-out group-hover:-translate-x-full">Über mich</span>
                        </Link>
                    </li>
                    <li>
                        <Link href="/" className='text-sm md:text-base hover:text-white transition-colors group relative inline-block overflow-hidden'>
                            <span className="absolute right-0 transition-all duration-500 ease-in-out translate-x-full group-hover:translate-x-0">Projects</span>
                            <span className="inline-block transition-all duration-500 ease-in-out group-hover:-translate-x-full">Projekte</span>
                        </Link>
                    </li>
                    <li>
                        <Link href="/contact" className='text-sm md:text-base hover:text-white transition-colors group relative inline-block overflow-hidden'>
                            <span className="absolute right-0 transition-all duration-500 ease-in-out translate-x-full group-hover:translate-x-0">Contact</span>
                            <span className="inline-block transition-all duration-500 ease-in-out group-hover:-translate-x-full">Kontakt</span>
                        </Link>
                    </li>
                    <li>
                        <Link href="/faq" className='text-sm md:text-base hover:text-white transition-colors group relative inline-block overflow-hidden'>
                            <span className="absolute right-0 transition-all duration-500 ease-in-out translate-x-full group-hover:translate-x-0">Shipping &amp; FAQ</span>
                            <span className="inline-block transition-all duration-500 ease-in-out group-hover:-translate-x-full">Versand &amp; FAQ</span>
                        </Link>
                    </li>
                </ul>
            </div>
            <div className='mb-4 md:mb-6 md:text-right'>
                <h3 className='mb-2 md:mb-3 uppercase text-white/90 font-semibold text-sm md:text-base'>Legal</h3>
                <ul className='space-y-1 md:space-y-2'>
                    <li>
                        <Link href="/impressum" className='text-sm md:text-base hover:text-white transition-colors group relative inline-block overflow-hidden'>
                            <span className="absolute right-0 transition-all duration-500 ease-in-out translate-x-full group-hover:translate-x-0">Imprint</span>
                            <span className="inline-block transition-all duration-500 ease-in-out group-hover:-translate-x-full">Impressum</span>
                        </Link>
                    </li>
                    <li>
                        <Link href="/terms" className='text-sm md:text-base hover:text-white transition-colors group relative inline-block overflow-hidden'>
                            <span className="absolute right-0 transition-all duration-500 ease-in-out translate-x-full group-hover:translate-x-0">Terms of Use</span>
                            <span className="inline-block transition-all duration-500 ease-in-out group-hover:-translate-x-full">AGBs</span>
                        </Link>
                    </li>
                    <li>
                        <Link href="/widerrufsbelehrung" className='text-sm md:text-base hover:text-white transition-colors group relative inline-block overflow-hidden'>
                            <span className="absolute right-0 transition-all duration-500 ease-in-out translate-x-full group-hover:translate-x-0">Right of Withdrawal</span>
                            <span className="inline-block transition-all duration-500 ease-in-out group-hover:-translate-x-full">Widerrufsbelehrung</span>
                        </Link>
                    </li>
                    <li>
                        <Link href="/privacy" className='text-sm md:text-base hover:text-white transition-colors group relative inline-block overflow-hidden'>
                            <span className="absolute right-0 transition-all duration-500 ease-in-out translate-x-full group-hover:translate-x-0">Privacy Policy</span>
                            <span className="inline-block transition-all duration-500 ease-in-out group-hover:-translate-x-full">Datenschutz</span>
                        </Link>
                    </li>
                </ul>
            </div>
            <div className='mt-auto md:text-right'>
                <h3 className='mb-2 md:mb-3 uppercase text-white/90 font-semibold text-sm md:text-base'>Follow me</h3>
                <div className='flex md:justify-end space-x-4'>
                    <a href="https://www.instagram.com/donataoppermann" className='text-white hover:text-white/80 transition-colors'>
                        <svg className="w-6 h-6 md:w-8 md:h-8" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" /></svg>
                    </a>
                </div>
            </div>
        </div>
    );
};

const MehrVonMir = () => {
    return (
        <div className='text-white/80 group'>
            <h2 className='text-xl md:text-3xl lg:text-5xl text-white/90 font-pp-neue leading-tight mb-3 relative inline-block overflow-hidden'>
                <span className="absolute right-0 transition-all duration-500 ease-in-out translate-x-full group-hover:translate-x-0">more about me</span>
                <span className="inline-block transition-all duration-500 ease-in-out group-hover:-translate-x-full">mehr von mir</span>
            </h2>
            <p className='text-sm md:text-base lg:text-lg leading-relaxed relative overflow-hidden'>
                <span className="block transition-all duration-500 ease-in-out group-hover:-translate-y-full">
                    Als Künstlerin erschaffe ich dreidimensionale Objekte, die durch gedrehte und vernähte Papierstreifen entstehen. Meine Kreationen sind von der Natur inspiriert und experimentieren mit Material, Licht und Raum. Das Spektrum reicht von zarten, lichtdurchlässigen Strukturen bis zu ausdrucksstarken Rauminstallationen. In weiteren Projekten verbinde ich Alltagsgegenstände mit organischen Formen aus gehäkeltem Papier zu überraschenden Objekten. Ergänzend gestalte ich künstlerischen Schmuck aus Papier und anderen Materialien.
                </span>
                <span className="absolute top-14 left-0 right-0 transition-all duration-500 ease-in-out translate-y-full group-hover:translate-y-0">
                    As an artist, I create three-dimensional objects from twisted and sewn paper strips. My creations, inspired by nature, experiment with material, light, and space. They range from delicate, translucent structures to expressive spatial installations. In other projects, I combine everyday objects with organic forms made of crocheted paper into surprising pieces. My installations incorporate prehistoric symbolism and connect it with current environmental themes. I also create architectural fragments from handmade paper that explore the concepts of space and shelter. Additionally, I design artistic jewelry using paper and other materials.
                </span>
            </p>
        </div>
    );
};

const ExhibitionCard = ({ exhibition }) => {
    const imageUrl = exhibition.image_url || DEFAULT_IMAGE;

    const formatDate = (dateString) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('de-DE', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        } catch (error) {
            console.error('Date formatting error:', error);
            return dateString;
        }
    };

    const startDate = formatDate(exhibition.start_date);
    const endDate = formatDate(exhibition.end_date);

    return (
        <div className='bg-white/10 overflow-hidden rounded-lg shadow-md transition-all duration-300 hover:shadow-xl hover:bg-white/20 flex h-24 md:h-28 mb-4'>
            <div className='relative w-24 md:w-28 h-full flex-shrink-0'>
                <Image
                    src={imageUrl}
                    alt={exhibition.title}
                    fill
                    sizes="(max-width: 768px) 96px, 112px"
                    className='transition-all duration-300 hover:opacity-80 object-cover'
                />
            </div>
            <div className='p-2 md:p-3 flex-grow flex flex-col justify-between'>
                <div>
                    <h4 className='font-bold text-xs md:text-sm mb-1 line-clamp-2'>{exhibition.title}</h4>
                    <p className='text-xs mb-1 text-white/80'>
                        {startDate} - {endDate}
                    </p>
                    {exhibition.location && (
                        <p className='text-xs text-white/60'>{exhibition.location}</p>
                    )}
                </div>
                {exhibition.url && (
                    <a
                        href={exhibition.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className='inline-block text-xs px-2 py-1 bg-white/20 text-white hover:bg-white/30 transition-colors duration-300 rounded self-start'
                    >
                        Mehr Info
                    </a>
                )}
            </div>
        </div>
    );
};

const UpcomingExhibitions = ({ exhibitions }) => {
    return (
        <div className='text-white h-full'>
            <h3 className='mb-4 uppercase text-white/90 font-semibold'>Termine und Ausstellungen</h3>
            <div className='space-y-4 md:max-h-[calc(75vh-10rem)] md:overflow-y-auto md:pr-2 custom-scrollbar'>
                {exhibitions && exhibitions.length > 0 ? (
                    exhibitions.map((exhibition) => (
                        <ExhibitionCard key={exhibition.id} exhibition={exhibition} />
                    ))
                ) : (
                    <p className="text-white/60 text-sm">
                        Aktuell sind keine Ausstellungen geplant.
                    </p>
                )}
            </div>
        </div>
    );
};
